import find from 'lodash-es/find.js'
import type { Countersink, CustomMaterial } from 'metalworks-domain'
import { AnyCountersinkHole, Hole, Plate, defaultCustomMaterials } from 'metalworks-domain'

import { isNonNullish } from '@salsita-ai/platform-common/utils'

import { configuratorInterface } from '../configurator-interface.js'

export const fixInvariantsTransformer = configuratorInterface.createTransformer(ctx => (updates, previousState) => {
  // TODO: check this is correct
  const plateUpdate = updates.find(
    u =>
      configuratorInterface.isAttributeSelectionUpdate(u, Plate.types, 'finish') ||
      configuratorInterface.isAttributeSelectionUpdate(u, Plate.types, 'finishDirection') ||
      configuratorInterface.isAttributeSelectionUpdate(u, Plate.types, 'handing') ||
      configuratorInterface.isAttributeSelectionUpdate(u, Plate.types, 'thickness') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'horizontalLength') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'verticalLength') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'cornerRadius') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'width') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'height') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'cornerRadius') ||
      configuratorInterface.isGenericDimensionUpdate(u, Plate.types, 'diameter') ||
      configuratorInterface.isSwatchSelectionUpdate(u, Plate.types, 'rawMaterial')
  )

  if (!plateUpdate && updates.length > 1) {
    return updates
  }

  const newUpdates = [...updates]

  const newState = ctx.storeSystem.getNewState(newUpdates, previousState)

  const valid = ctx.validationSystem.validateState(newState)

  // Needed to narrow types, it is a bit unfortunate we have the uncaught case there
  if (valid.severity === 'uncaught') {
    return updates
  }

  const $plateEntity = ctx.domain.getRoot$Entity(newState)

  if (!$plateEntity) {
    throw new Error('Plate entity not found')
  }

  if (!valid.valid && plateUpdate) {
    for (const violation of valid.entityViolations) {
      // TODO: this can be separate transformer just for the dimension updates
      if (violation.violation.type === 'closeToEdge') {
        const entity = previousState.entities.find(e => e.id === violation.entityId)
        if (entity === undefined || !Hole.is(entity) || plateUpdate.kind !== 'modifyComponent') {
          continue
        }

        const maxFeatureSize = $plateEntity.$maxFeatureSize
        const $entity = ctx.domain.get$Entity(entity.id, newState)

        // If the feature is bigger than the plate, we can't move it.
        if ($entity.$sizeX > maxFeatureSize.x || $entity.$sizeY > maxFeatureSize.y) {
          continue
        }

        // @ts-expect-error TODO: this transformer should be split to multiple transformers so it can be narrowed
        const newValue = plateUpdate.writableState.value as number
        const fixUpdate = configuratorInterface.updates.DomainEntityModification({
          entityId: violation.entityId as any,
          stateDelta: {
            x: newValue - $plateEntity.$minimumDistanceHoleToEdge - $entity.$sizeX / 2,
          },
          updateTag: 'fix.moveHoles',
        })

        return [fixUpdate]
      }
    }
  }

  // TODO: this can be separate transformer just for the updates that may influence this
  if (!valid.valid && valid.entityViolations.some(v => v.violation.type === 'countersinkNotAvailable')) {
    const availableCountersinks = $plateEntity.$availableCountersinks
    const availableCountersingIds = availableCountersinks.map(c => c.id)

    newState.entities.forEach(e => {
      if (AnyCountersinkHole.is(e) && !availableCountersingIds.includes(e.id)) {
        const firstCountersink = availableCountersinks[0]

        if (firstCountersink) {
          newUpdates.push(
            configuratorInterface.updates.DomainEntityModification({
              entityId: e.id,
              stateDelta: {
                countersinkId: firstCountersink.id,
                topRadius: firstCountersink.topRadius,
                bottomRadius: firstCountersink.bottomRadius,
              },
              updateTag: 'fix.updateCountersink',
            })
          )
        }
      }
    })
  }

  // TODO: this can be separate transformer just for material update
  if (
    plateUpdate?.kind === 'modifyComponent' &&
    (plateUpdate.componentInstance.instanceName as string) === 'rawMaterial'
  ) {
    const writableState = plateUpdate.writableState as { selectedSwatchKey: string }

    // Try to find material with the same thickness first to make countersinks work.
    let material = $plateEntity.$customMaterials.find(
      m => m.rawMaterial === writableState.selectedSwatchKey && m.thickness === $plateEntity.thickness
    )

    // Try to find material which also has any countersink type available.
    if (!material) {
      material = $plateEntity.$customMaterials.find(
        m =>
          m.rawMaterial === writableState.selectedSwatchKey &&
          $plateEntity.$allCountersinks.some(
            c => c.plateThicknessMin <= m.thickness && m.thickness <= c.plateThicknessMax
          )
      )
    }

    if (!material) {
      material = $plateEntity.$customMaterials.find(m => m.rawMaterial === writableState.selectedSwatchKey)
    }

    if (material) {
      const fixUpdate = configuratorInterface.updates.DomainEntityModification({
        entityId: $plateEntity.id,
        stateDelta: {
          rawMaterial: material.rawMaterial,
          finish: material.finish,
          thickness: material.thickness,
        },
        updateTag: 'fix.material',
      })

      return [fixUpdate]
    }
  }

  // TODO: this can be separate transformer just for finish update
  if (plateUpdate?.kind === 'modifyComponent' && (plateUpdate.componentInstance.instanceName as string) === 'finish') {
    const writableState = plateUpdate.writableState as { value: string }

    const material = $plateEntity.$customMaterials.find(
      m => m.rawMaterial === $plateEntity.rawMaterial && m.finish === writableState.value
    )

    if (material) {
      const fixUpdate = configuratorInterface.updates.DomainEntityModification({
        entityId: $plateEntity.id,
        stateDelta: {
          rawMaterial: material.rawMaterial,
          finish: material.finish,
          thickness: material.thickness,
        },
        updateTag: 'fix.finish',
      })

      return [fixUpdate]
    } else {
      return updates
    }
  }

  // TODO: this should be a separate transformer for simplicity - it does not correlate with the rest of the logic

  // Hack to see if this is initial update, may have some edge cases, but I couldn't break it
  if (updates.length === 0 && !valid.valid) {
    // Check if toggles for countersink picked unavailable countersink.
    // If so, pick another material.
    const countersingNotAvailable = valid.entityViolations.some(v => v.violation.type === 'countersinkNotAvailable')

    if (countersingNotAvailable) {
      // Find first material which has any countersink type available.
      // This needs to be improved, but we're not sure what should be the logic behind this.

      let countersink: Countersink | undefined
      let material: CustomMaterial | undefined
      for (const c of $plateEntity.$allCountersinks) {
        for (const m of $plateEntity.$customMaterials) {
          if (c.plateThicknessMin <= m.thickness && m.thickness <= c.plateThicknessMax) {
            countersink = c
            material = m
            break
          }
        }
      }

      if (material && countersink) {
        const fixUpdatePlate = configuratorInterface.updates.DomainEntityModification({
          entityId: $plateEntity.id,
          stateDelta: {
            rawMaterial: material.rawMaterial,
            finish: material.finish,
            thickness: material.thickness,
          },
          updateTag: 'fix.initial',
        })

        const fixUpdateCountersinks = newState.entities
          .map(e => {
            if (AnyCountersinkHole.is(e)) {
              return configuratorInterface.updates.DomainEntityModification({
                entityId: e.id,
                stateDelta: {
                  countersinkId: countersink.id,
                  topRadius: countersink.topRadius,
                  bottomRadius: countersink.bottomRadius,
                },
                updateTag: 'fix.initial',
              })
            }

            return null
          })
          .filter(isNonNullish)

        // TODO: This really needs some way how to notify user about this.
        return [fixUpdatePlate, ...fixUpdateCountersinks]
      }
    }

    // Check if toggles for material picked unavailable material combination.
    // If so, pick new material which keeps as many properties unchanged as possible.
    const materialNotAvailable = valid.entityViolations.some(v => v.violation.type === 'materialNotAvailable')

    if (materialNotAvailable) {
      const materialFilter = {
        rawMaterial: $plateEntity.rawMaterial,
        finish: $plateEntity.finish,
        thickness: $plateEntity.thickness,
      } satisfies Partial<Plate>

      const newMaterial =
        (find($plateEntity.$customMaterials, materialFilter) as CustomMaterial) ??
        $plateEntity.$customMaterials[0] ??
        defaultCustomMaterials[0]!

      const fixUpdate = configuratorInterface.updates.DomainEntityModification({
        entityId: $plateEntity.id,
        stateDelta: {
          rawMaterial: newMaterial.rawMaterial,
          finish: newMaterial.finish,
          thickness: newMaterial.thickness,
        },
        updateTag: 'fix.initial',
      })

      return [fixUpdate]
    }
  }

  return newUpdates
})
